import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Search } from "lucide-react";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { formatInteger } from "@/lib/format";

export type SortDirection = "asc" | "desc";
export type SortState<K extends string> = { key: K; direction: SortDirection };
type SortValue = string | number | boolean | Date | null | undefined;

export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);

  return debounced;
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function comparable(value: SortValue): string | number {
  if (value == null) return "";
  if (value instanceof Date) return value.getTime();
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "number") return value;
  return value.toLowerCase();
}

function compareValues(a: SortValue, b: SortValue): number {
  const av = comparable(a);
  const bv = comparable(b);
  if (typeof av === "number" && typeof bv === "number") return av - bv;
  return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
}

export function useAdminTable<T, K extends string>({
  rows,
  search,
  initialSort,
  sorters,
  searchText,
  filters = [],
}: {
  rows: readonly T[];
  search: string;
  initialSort: SortState<K>;
  sorters: Record<K, (row: T) => SortValue>;
  searchText: (row: T) => string;
  filters?: Array<(row: T) => boolean>;
}) {
  const [sort, setSort] = useState<SortState<K>>(initialSort);
  const debouncedSearch = useDebouncedValue(search);

  const tableRows = useMemo(() => {
    const q = normalize(debouncedSearch);
    return rows
      .filter((row) => filters.every((fn) => fn(row)))
      .filter((row) => !q || normalize(searchText(row)).includes(q))
      .slice()
      .sort((a, b) => {
        const result = compareValues(sorters[sort.key](a), sorters[sort.key](b));
        return sort.direction === "asc" ? result : -result;
      });
  }, [debouncedSearch, filters, rows, searchText, sort.direction, sort.key, sorters]);

  const requestSort = (key: K) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };

  return { rows: tableRows, sort, requestSort, debouncedSearch };
}

export function AdminTableControls({
  search,
  onSearchChange,
  placeholder,
  totalCount,
  resultCount,
  children,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  placeholder: string;
  totalCount: number;
  resultCount: number;
  children?: ReactNode;
}) {
  return (
    <div style={styles.wrap}>
      <label style={styles.searchLabel}>
        <Search size={15} aria-hidden="true" />
        <input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={placeholder}
          style={styles.searchInput}
        />
      </label>
      {children}
      <span style={styles.count}>
        Showing {formatInteger(resultCount)} of {formatInteger(totalCount)}
      </span>
    </div>
  );
}

export function AdminTableFilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ label: string; value: string }>;
}) {
  return (
    <label style={styles.selectLabel}>
      <span style={styles.selectText}>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} style={styles.select}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function SortableTh<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: K;
  sort: SortState<K>;
  onSort: (key: K) => void;
  align?: "left" | "right";
}) {
  const active = sort.key === sortKey;
  return (
    <th style={{ textAlign: align }}>
      <button type="button" onClick={() => onSort(sortKey)} style={styles.sortButton}>
        <span>{label}</span>
        <span style={active ? styles.sortMarkActive : styles.sortMark}>
          {active ? sort.direction : ""}
        </span>
      </button>
    </th>
  );
}

const styles = {
  wrap: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "wrap",
    marginBottom: "0.85rem",
  },
  searchLabel: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    flex: "1 1 260px",
    maxWidth: 420,
    padding: "0.55rem 0.8rem",
    border: "1px solid rgba(201,147,76,0.40)",
    borderRadius: 8,
    background: "#fff",
    color: colors.navy,
    fontFamily: fonts.body,
  },
  searchInput: {
    width: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    color: colors.navy,
    fontFamily: fonts.body,
    fontSize: 13.5,
    fontWeight: 600,
  },
  selectLabel: {
    display: "flex",
    alignItems: "center",
    gap: "0.45rem",
    fontFamily: fonts.body,
  },
  selectText: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.1em",
    color: "rgba(8,45,61,0.58)",
  },
  select: {
    minWidth: 140,
    padding: "0.5rem 0.7rem",
    border: "1px solid rgba(201,147,76,0.40)",
    borderRadius: 8,
    background: "#fff",
    color: colors.navy,
    fontFamily: fonts.body,
    fontSize: 13,
    fontWeight: 700,
  },
  count: {
    marginLeft: "auto",
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 700,
    color: "rgba(8,45,61,0.55)",
  },
  sortButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    width: "100%",
    border: "none",
    background: "transparent",
    padding: 0,
    color: "inherit",
    cursor: "pointer",
    font: "inherit",
    letterSpacing: "inherit",
    textTransform: "inherit" as const,
    textAlign: "inherit" as const,
  },
  sortMark: {
    minWidth: 26,
    opacity: 0.35,
    fontSize: 8,
    letterSpacing: "0.04em",
  },
  sortMarkActive: {
    minWidth: 26,
    opacity: 0.8,
    fontSize: 8,
    letterSpacing: "0.04em",
  },
} satisfies Record<string, CSSProperties>;
